/**
 * De kleine bouwstenen die overal terugkomen: kroon, niveaubadge, knoppen,
 * velden, meldingen. Eén bestand, zodat een pagina met één import toe kan.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { NIVEAUNAMEN, type Niveau } from '../lib/niveau';
import { alsEuro } from '../lib/geld';
import { alsNederlandseDatum } from '../lib/datum';

export function Kroon({ className = 'h-8 w-8' }: { className?: string }) {
  return <span aria-hidden="true" className={`kroon ${className}`} />;
}

export const NIVEAUKLEUR: Record<Niveau, { tekst: string; vlak: string; rand: string }> = {
  brons: { tekst: 'text-brons', vlak: 'bg-brons-zacht', rand: 'border-brons/40' },
  zilver: { tekst: 'text-zilver', vlak: 'bg-zilver-zacht', rand: 'border-zilver/40' },
  goud: { tekst: 'text-goud', vlak: 'bg-goud-zacht', rand: 'border-goud/50' },
};

export function Niveaubadge({ niveau, groot = false }: { niveau: Niveau; groot?: boolean }) {
  const k = NIVEAUKLEUR[niveau];
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border ${k.rand} ${k.vlak} ${k.tekst} font-semibold uppercase tracking-wider ${
        groot ? 'px-3 py-1 text-xs' : 'px-2 py-0.5 text-[0.65rem]'
      }`}
    >
      <Kroon className={groot ? 'h-3.5 w-3.5' : 'h-2.5 w-2.5'} />
      {NIVEAUNAMEN[niveau]}
    </span>
  );
}

export function Knop({
  children,
  soort = 'goud',
  klein = false,
  bezig = false,
  type = 'button',
  naar,
  onClick,
  disabled,
  className = '',
}: {
  children: ReactNode;
  soort?: 'goud' | 'stil' | 'rood';
  klein?: boolean;
  bezig?: boolean;
  type?: 'button' | 'submit';
  naar?: string;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
}) {
  const klassen = `knop knop-${soort} ${klein ? 'knop-klein' : ''} ${className}`;
  if (naar) {
    return (
      <Link to={naar} className={klassen}>
        {children}
      </Link>
    );
  }
  return (
    <button type={type} className={klassen} onClick={onClick} disabled={disabled || bezig}>
      {bezig ? 'Even geduld…' : children}
    </button>
  );
}

export function Veld({
  etiket,
  hulp,
  fout,
  children,
}: {
  etiket: string;
  hulp?: string;
  fout?: string | null;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="etiket">{etiket}</span>
      {children}
      {hulp && !fout && <span className="mt-1.5 block text-sm text-inkt-zacht">{hulp}</span>}
      {fout && <span className="mt-1.5 block text-sm text-rood">{fout}</span>}
    </label>
  );
}

export function Melding({ soort = 'info', children }: { soort?: 'info' | 'fout' | 'goed'; children: ReactNode }) {
  const kleur =
    soort === 'fout'
      ? 'border-rood/40 bg-rood-zacht text-rood'
      : soort === 'goed'
        ? 'border-groen/40 bg-groen-zacht text-groen'
        : 'border-goud-600/40 bg-goud-zacht text-goud-300';
  return (
    <div role={soort === 'fout' ? 'alert' : 'status'} className={`rounded-xl border px-4 py-3 text-sm ${kleur}`}>
      {children}
    </div>
  );
}

export function Laden({ tekst = 'Even laden…' }: { tekst?: string }) {
  return (
    <div className="flex items-center gap-3 py-16 text-inkt-zacht">
      <Kroon className="h-5 w-5 animate-pulse text-goud-600" />
      {tekst}
    </div>
  );
}

export function Euro({ cent }: { cent: number }) {
  return <span className="tabular-nums">{alsEuro(cent)}</span>;
}

export function Datum({ waarde }: { waarde: string | null | undefined }) {
  if (!waarde) return <span className="text-inkt-stil">-</span>;
  return <time dateTime={waarde}>{alsNederlandseDatum(waarde.slice(0, 10))}</time>;
}

/** De paginatitel in het tabblad. */
export function useTitel(titel: string) {
  useEffect(() => {
    const vorige = document.title;
    document.title = titel ? `${titel} - Helden van de Hilfertheem` : 'Helden van de Hilfertheem';
    return () => {
      document.title = vorige;
    };
  }, [titel]);
}

/** Een knop die na een bevestiging pas iets doet. */
export function Bevestigknop({
  children,
  vraag,
  onBevestig,
  soort = 'rood',
  klein = true,
}: {
  children: ReactNode;
  vraag: string;
  onBevestig: () => Promise<void> | void;
  soort?: 'goud' | 'stil' | 'rood';
  klein?: boolean;
}) {
  const [vraagt, zetVraagt] = useState(false);
  const [bezig, zetBezig] = useState(false);
  if (!vraagt) {
    return (
      <Knop soort={soort} klein={klein} onClick={() => zetVraagt(true)}>
        {children}
      </Knop>
    );
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-2 text-sm">
      <span className="text-inkt-zacht">{vraag}</span>
      <Knop
        soort={soort}
        klein
        bezig={bezig}
        onClick={async () => {
          zetBezig(true);
          try {
            await onBevestig();
          } finally {
            zetBezig(false);
            zetVraagt(false);
          }
        }}
      >
        Ja
      </Knop>
      <Knop soort="stil" klein onClick={() => zetVraagt(false)}>
        Nee
      </Knop>
    </span>
  );
}
